import { useState, useRef, useEffect, useMemo } from "react";
import { Select, Picto, toast } from "./ui";
import { loadPatientsCache } from "./lib/patientStore";
import { loadRecallsCache } from "./lib/recallStore";
import { loadAptsCache, listApts, upsertApt as storeUpsertApt, deleteApt as storeDeleteApt, saveAllApts } from "./lib/aptStore";
import { getBehandler, loadSettings } from "./lib/settingsStore";
import { demoFallbacks, serverActive } from "./lib/mode";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
export const DEFAULT_TYPES = [
  { id:"kontrolle", label:"Kontrolle", color:"#2563EB", bg:"#DBEAFE" },
  { id:"schmerz",   label:"Schmerz",   color:"#DC2626", bg:"#FEE2E2" },
  { id:"endo",      label:"Endo",      color:"#D97706", bg:"rgba(245,158,11,0.14)" },
  { id:"fuellung",  label:"Füllung",   color:"#16A34A", bg:"#DCFCE7" },
  { id:"prothetik", label:"Prothetik", color:"#C77D1A", bg:"rgba(245,158,11,0.14)" },
  { id:"prophylaxe",label:"Prophylaxe",color:"#7C3AED", bg:"#EDE9FE" },
];

// Demo-Standardliste — die aktive Liste kommt aus den Praxis-Einstellungen
export const BEHANDLER = [
  { id:"dh",  name:"Dr. Hoffmann",  short:"DH",  color:"#0F6E56" },
  { id:"ds",  name:"Dr. Schulz",    short:"DS",  color:"#185FA5" },
  { id:"lbe", name:"Dr. Bergmann",  short:"LBe", color:"#7C3AED" },
  { id:"tf",  name:"Dr. Fischer",   short:"TF",  color:"#C77D1A" },
  { id:"mr",  name:"Maja Roth",     short:"MR",  color:"#166534" },
];

const ROOMS = ["Zimmer 1","Zimmer 2","Behandlung A","Behandlung B","Labor","Kein Zimmer"];

const DAYS_DE   = ["So","Mo","Di","Mi","Do","Fr","Sa"];
const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

const SLOT_H    = 28;
const SLOT_MINS = 15;

// Patientenliste für die Autocomplete — gleiche Quelle wie der Patienten-Screen
// (localStorage "dida_patients", Fallback: zentrale Demo-Stammdaten)
const getKnownPatients = () =>
  loadPatientsCache().map(p=>({ name:`${p.nachname}, ${p.vorname}`, dob:p.dob, kv:p.kv, allergy:p.allergy&&p.allergy!=="—"?p.allergy:"" }));

const INITIAL_APTS = [
  { id:1, date:"2026-06-07", time:"08:00", dur:30, patient:"Klaus Weber",    dob:"05.11.1954", allergy:"", behandler:"dh", type:"kontrolle", anliegen:"Kontrolluntersuchung", status:"checkin",  src:"Online",    kv:"TK",     notes:"", room:"Zimmer 1", waitSince:null },
  { id:2, date:"2026-06-07", time:"08:00", dur:60, patient:"Maria Maier",    dob:"12.03.1967", allergy:"Penicillin", behandler:"dh", type:"schmerz", anliegen:"Starke Zahnschmerzen", status:"wartend", src:"KI-Tel.", kv:"AOK", notes:"", room:"", waitSince:Date.now()-8*60000 },
  { id:3, date:"2026-06-07", time:"08:00", dur:90, patient:"Hans Schmidt",   dob:"08.04.1956", allergy:"", behandler:"ds", type:"endo",      anliegen:"Wurzelbehandlung 36",   status:"checkin",  src:"Online",    kv:"BKK",    notes:"", room:"Zimmer 2", waitSince:null },
  { id:4, date:"2026-06-07", time:"09:30", dur:45, patient:"Anna Fischer",   dob:"28.07.1989", allergy:"", behandler:"ds", type:"fuellung",  anliegen:"Komposit 14",           status:"gebucht",  src:"Online",    kv:"Privat", notes:"", room:"", waitSince:null },
  { id:5, date:"2026-06-07", time:"08:00", dur:60, patient:"Lena Bauer",     dob:"14.05.1995", allergy:"", behandler:"mr",  type:"prophylaxe",anliegen:"PZR + Fluorid",         status:"wartend",  src:"Online",    kv:"TK",     notes:"", room:"", waitSince:Date.now()-3*60000 },
  { id:6, date:"2026-06-07", time:"09:30", dur:30, patient:"Eva Richter",    dob:"30.09.1965", allergy:"", behandler:"tf",  type:"kontrolle", anliegen:"Kontrolle nach Extr.",  status:"gebucht",  src:"KI-Tel.",   kv:"AOK",    notes:"", room:"", waitSince:null },
  { id:7, date:"2026-06-07", time:"10:00", dur:30, patient:"Paul Müller",    dob:"11.12.1975", allergy:"", behandler:"dh",  type:"fuellung",  anliegen:"Füllung 36",            status:"abgesagt", src:"Online",    kv:"AOK",    notes:"Patient rief 09:45 an.", room:"", cancelReason:"Beruflicher Termin", waitSince:null },
  { id:8, date:"2026-06-08", time:"09:00", dur:60, patient:"Max Braun",      dob:"14.02.1978", allergy:"Latex", behandler:"dh", type:"prothetik", anliegen:"Krone einsetzen 26", status:"gebucht", src:"Online", kv:"BKK", notes:"", room:"", waitSince:null },
  { id:9, date:"2026-06-07", time:"10:30", dur:45, patient:"Tim Schreier",   dob:"03.07.2012", allergy:"", behandler:"lbe", type:"prophylaxe",anliegen:"KFO-Kontrolle Phase 3", status:"gebucht",  src:"Online",    kv:"TK",     notes:"", room:"", waitSince:null },
  { id:10,date:"2026-06-07", time:"11:15", dur:30, patient:"Felix Graf",     dob:"22.11.1998", allergy:"", behandler:"tf",  type:"fuellung",  anliegen:"Füllung 15",            status:"gebucht",  src:"KI-Tel.",   kv:"AOK",    notes:"", room:"", waitSince:null },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const dateKey = d => d instanceof Date ? d.toISOString().slice(0,10) : d;
const addDays  = (d,n) => { const r=new Date(d); r.setDate(r.getDate()+n); return r; };
const startOfWeek = d => { const r=new Date(d); r.setDate(r.getDate()-((r.getDay()+6)%7)); return r; };
const timeToMins  = t => { const [h,m]=t.split(":").map(Number); return h*60+m; };
const minsToTime  = m => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
const fmtWait     = ms => { const m=Math.floor(ms/60000); return m<1?"<1 Min.":`${m} Min.`; };

function buildSlots(startH, endH) {
  const s=[];
  for(let h=startH;h<endH;h++) for(let m=0;m<60;m+=15) s.push(`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`);
  return s;
}
function timeToTop(t, startH) { return (timeToMins(t)-startH*60)/SLOT_MINS*SLOT_H; }
const durToH = dur => dur/SLOT_MINS*SLOT_H;

function computeLayout(apts) {
  const sorted=[...apts].sort((a,b)=>timeToMins(a.time)-timeToMins(b.time));
  const columns=[];
  const result={};
  sorted.forEach(apt=>{
    const s=timeToMins(apt.time), _e=s+apt.dur; // eslint-disable-line no-unused-vars
    let placed=false;
    for(let c=0;c<columns.length;c++){
      const last=columns[c][columns[c].length-1];
      if(s>=timeToMins(last.time)+last.dur){columns[c].push(apt);placed=true;break;}
    }
    if(!placed) columns.push([apt]);
  });
  sorted.forEach(apt=>{
    const s=timeToMins(apt.time),e=s+apt.dur;
    let col=0;
    columns.forEach((ca,ci)=>ca.forEach(a=>{if(a.id===apt.id)col=ci;}));
    const overlapCols=columns.filter(ca=>ca.some(a=>{const as=timeToMins(a.time);return as<e&&as+a.dur>s;})).length;
    result[apt.id]={col,totalCols:overlapCols};
  });
  return result;
}

// ─── SEGMENTED INPUTS ─────────────────────────────────────────────────────────
function SegInput({parts,seps,onChange,widths,placeholders,style}) {
  const r0=useRef(null),r1=useRef(null),r2=useRef(null),r3=useRef(null);
  const refs=[r0,r1,r2,r3];
  return (
    <div style={{display:"flex",alignItems:"center",gap:3}}>
      {parts.map((val,i)=>(
        <span key={i} style={{display:"flex",alignItems:"center",gap:3}}>
          <input
            ref={refs[i]}
            value={val}
            onChange={e=>{
              const c=e.target.value.replace(/\D/g,"");
              const np=[...parts]; np[i]=c; onChange(np);
              if(c.length===widths[i]&&refs[i+1]?.current) refs[i+1].current.select();
            }}
            onKeyDown={e=>{
              if(e.key==="Backspace"&&e.target.value===""&&refs[i-1]?.current){
                e.preventDefault(); refs[i-1].current.select();
              }
            }}
            onFocus={e=>e.target.select()}
            onClick={e=>e.target.select()}
            placeholder={placeholders[i]}
            maxLength={widths[i]}
            inputMode="numeric"
            style={{...style,width:widths[i]===4?56:36,textAlign:"center",padding:"9px 4px"}}
          />
          {seps[i]&&<span style={{color:"var(--mf-border2)",fontSize:16,userSelect:"none"}}>{seps[i]}</span>}
        </span>
      ))}
    </div>
  );
}

function DobInput({value,onChange,style}) {
  const p=(value||"").split("."); const dd=p[0]||"",mm=p[1]||"",yy=p[2]||"";
  const dobSeps=[".",".",""];
  return <SegInput parts={[dd,mm,yy]} seps={dobSeps} widths={[2,2,4]} placeholders={["TT","MM","JJJJ"]} style={style} onChange={([d,m,y])=>onChange([d,m,y].join("."))} />;
}

function TimeInput({value,onChange,style}) {
  const p=(value||"").split(":"); const hh=p[0]||"",mm=p[1]||"";
  const timeSeps=[":",""];
  return <SegInput parts={[hh,mm]} seps={timeSeps} widths={[2,2]} placeholders={["HH","MM"]} style={style} onChange={([h,m])=>onChange(h+":"+m)} />;
}

function DateInput({value,onChange,style,today}) {
  const [showCal,setShowCal]=useState(false);
  const dobSeps=[".",".",""];
  const p=(value||"").split("-"); const yy=p[0]||"",mm=p[1]||"",dd=p[2]||"";
  return (
    <div style={{position:"relative"}}>
      <div style={{display:"flex",alignItems:"center",gap:3}}>
        <SegInput parts={[dd,mm,yy]} seps={dobSeps} widths={[2,2,4]} placeholders={["TT","MM","JJJJ"]} style={style} onChange={([d,m,y])=>onChange([y,m,d].join("-"))} />
        <button onClick={()=>setShowCal(v=>!v)} style={{marginLeft:4,width:30,height:30,borderRadius:7,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Picto.Calendar size={15} stroke="var(--mf-muted)"/></button>
      </div>
      {showCal&&<>
        <div onClick={()=>setShowCal(false)} style={{position:"fixed",inset:0,zIndex:49}}/>
        <MiniCalendar current={value?new Date(value):new Date()} today={today||new Date()} onSelect={d=>{const y=d.getFullYear(),m=String(d.getMonth()+1).padStart(2,"0"),dd2=String(d.getDate()).padStart(2,"0");onChange(`${y}-${m}-${dd2}`);setShowCal(false);}} onClose={()=>setShowCal(false)}/>
      </>}
    </div>
  );
}

// ─── MINI CALENDAR ────────────────────────────────────────────────────────────
function MiniCalendar({current,today,onSelect,onClose}) {
  const [view,setView]=useState("days");
  const [disp,setDisp]=useState(new Date(current));
  const yr=disp.getFullYear(), mo=disp.getMonth();
  const firstDay=new Date(yr,mo,1), pad=(firstDay.getDay()+6)%7, dim=new Date(yr,mo+1,0).getDate();
  const cells=[...Array(pad).fill(null),...Array.from({length:dim},(_,i)=>new Date(yr,mo,i+1))];
  const nav=n=>setDisp(d=>view==="days"?new Date(d.getFullYear(),d.getMonth()+n,1):view==="months"?new Date(d.getFullYear()+n,d.getMonth(),1):new Date(d.getFullYear()+n*10,d.getMonth(),1));
  return (
    <div style={{position:"absolute",top:"100%",left:0,zIndex:50,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,.15)",padding:12,width:236,marginTop:4}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <button onClick={()=>nav(-1)} style={mbs}>←</button>
        <div style={{display:"flex",gap:5}}>
          <button onClick={()=>setView(v=>v==="months"?"days":"months")} style={{...mbs,fontWeight:600}}>{MONTHS_DE[mo].slice(0,3)}.</button>
          <button onClick={()=>setView(v=>v==="years"?"days":"years")} style={{...mbs,fontWeight:600}}>{yr}</button>
        </div>
        <button onClick={()=>nav(1)} style={mbs}>→</button>
      </div>
      {view==="days"&&<>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4}}>
          {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=><div key={d} style={{fontSize:9,color:"var(--mf-muted)",textAlign:"center",padding:"2px 0"}}>{d}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {cells.map((d,i)=>{
            if(!d) return <div key={i}/>;
            const isTod=dateKey(d)===dateKey(today), isSel=dateKey(d)===dateKey(current);
            return <button key={i} onClick={()=>{onSelect(d);onClose();}} style={{fontSize:11,padding:"4px 0",borderRadius:10,border:"none",background:isSel?"#0F6E56":isTod?"#E1F5EE":"transparent",color:isSel?"#fff":isTod?"#0F6E56":"var(--mf-text)",cursor:"pointer",fontWeight:isTod?600:400,fontFamily:"inherit"}}>{d.getDate()}</button>;
          })}
        </div>
        <div style={{borderTop:"0.5px solid var(--mf-border)",marginTop:8,paddingTop:6,textAlign:"center"}}>
          <button onClick={()=>{onSelect(today);onClose();}} style={{fontSize:10,padding:"3px 12px",borderRadius:20,border:"0.5px solid #0F6E56",background:"#E1F5EE",color:"#085041",cursor:"pointer",fontFamily:"inherit"}}>Heute</button>
        </div>
      </>}
      {view==="months"&&<div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:4}}>
        {MONTHS_DE.map((m,i)=><button key={i} onClick={()=>{setDisp(new Date(yr,i,1));setView("days");}} style={{fontSize:10,padding:"6px 4px",borderRadius:10,border:"none",background:i===mo?"#0F6E56":"var(--mf-bg-alt)",color:i===mo?"#fff":"var(--mf-text)",cursor:"pointer",fontFamily:"inherit"}}>{m.slice(0,3)}</button>)}
      </div>}
      {view==="years"&&<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4}}>
        {Array.from({length:12},(_,i)=>yr-5+i).map(y=><button key={y} onClick={()=>{setDisp(new Date(y,mo,1));setView("months");}} style={{fontSize:10,padding:"6px 2px",borderRadius:10,border:"none",background:y===yr?"#0F6E56":"var(--mf-bg-alt)",color:y===yr?"#fff":"var(--mf-text)",cursor:"pointer",fontFamily:"inherit"}}>{y}</button>)}
      </div>}
    </div>
  );
}
const mbs={fontSize:11,padding:"3px 8px",borderRadius:10,border:"0.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit"};

// ─── SETTINGS MODAL ───────────────────────────────────────────────────────────
function SettingsModal({types,onSave,onClose,dayConfig,onSaveDayConfig}) {
  const [local,setLocal]=useState(types.map(t=>({...t})));
  const [newLabel,setNewLabel]=useState(""); const [newColor,setNewColor]=useState("#6366f1");
  const [dayCfg,setDayCfg]=useState(dayConfig);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:16,width:480,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        <div style={{padding:"16px 20px 0",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:700}}>Einstellungen</div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",border:"none",background:"var(--mf-bg-alt)",color:"var(--mf-muted)",cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>×</button>
        </div>
        <div style={{padding:"0 20px 20px",display:"flex",flexDirection:"column",gap:16}}>
          {/* Day hours */}
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"var(--mf-text)",marginBottom:8}}>Öffnungszeiten je Wochentag</div>
            {[1,2,3,4,5,6,0].map(idx=>{
              const day=DAYS_DE[idx];
              const cfg=dayCfg[idx]||{start:7,end:20,closed:false};
              return (
                <div key={idx} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                  <span style={{fontSize:11,fontWeight:500,width:28,color:"var(--mf-muted)"}}>{day}</span>
                  <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--mf-muted)",cursor:"pointer"}}>
                    <input type="checkbox" checked={cfg.closed} onChange={e=>setDayCfg(p=>({...p,[idx]:{...cfg,closed:e.target.checked}}))} style={{cursor:"pointer"}}/>
                    Geschlossen
                  </label>
                  {!cfg.closed&&<>
                    <select value={cfg.start} onChange={e=>setDayCfg(p=>({...p,[idx]:{...cfg,start:+e.target.value}}))} style={{fontSize:11,padding:"3px 6px",borderRadius:10,border:"1.5px solid var(--mf-border)",fontFamily:"inherit"}}>
                      {Array.from({length:14},(_,i)=>i+6).map(h=><option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
                    </select>
                    <span style={{fontSize:11,color:"var(--mf-muted)"}}>bis</span>
                    <select value={cfg.end} onChange={e=>setDayCfg(p=>({...p,[idx]:{...cfg,end:+e.target.value}}))} style={{fontSize:11,padding:"3px 6px",borderRadius:10,border:"1.5px solid var(--mf-border)",fontFamily:"inherit"}}>
                      {Array.from({length:16},(_,i)=>i+8).map(h=><option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
                    </select>
                  </>}
                </div>
              );
            })}
          </div>
          {/* Termintypen */}
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"var(--mf-text)",marginBottom:8}}>Termintypen</div>
            {local.map(t=>(
              <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",background:"var(--mf-bg-alt)",borderRadius:7,marginBottom:5}}>
                <input type="color" value={t.color} onChange={e=>setLocal(p=>p.map(x=>x.id===t.id?{...x,color:e.target.value,bg:e.target.value+"22"}:x))} style={{width:28,height:28,borderRadius:10,border:"none",cursor:"pointer",padding:0}}/>
                <input value={t.label} onChange={e=>setLocal(p=>p.map(x=>x.id===t.id?{...x,label:e.target.value}:x))} style={{flex:1,fontSize:12,padding:"4px 7px",borderRadius:10,border:"0.5px solid var(--mf-border2)",fontFamily:"inherit"}}/>
                <button onClick={()=>setLocal(p=>p.filter(x=>x.id!==t.id))} style={{fontSize:11,padding:"3px 9px",borderRadius:10,border:"0.5px solid #fca5a5",background:"rgba(220,38,38,0.10)",color:"var(--mf-neg)",cursor:"pointer",fontFamily:"inherit"}}>×</button>
              </div>
            ))}
            <div style={{display:"flex",gap:7,alignItems:"center",marginTop:6}}>
              <input type="color" value={newColor} onChange={e=>setNewColor(e.target.value)} style={{width:32,height:32,border:"none",cursor:"pointer",padding:0,borderRadius:10}}/>
              <input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Neuer Typ…" style={{flex:1,fontSize:12,padding:"5px 8px",borderRadius:10,border:"0.5px solid var(--mf-border2)",fontFamily:"inherit"}}/>
              <button onClick={()=>{if(!newLabel.trim())return;setLocal(p=>[...p,{id:Date.now().toString(),label:newLabel,color:newColor,bg:newColor+"22"}]);setNewLabel("");}} style={{fontSize:11,padding:"5px 12px",borderRadius:10,background:"#0F6E56",color:"#fff",border:"none",cursor:"pointer",fontFamily:"inherit"}}>+ Add</button>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:4,borderTop:"0.5px solid var(--mf-border)"}}>
            <button onClick={onClose} style={{fontSize:11,padding:"7px 16px",borderRadius:12,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit"}}>Abbrechen</button>
            <button onClick={()=>{onSave(local);onSaveDayConfig(dayCfg);onClose();}} style={{fontSize:11,padding:"7px 16px",borderRadius:12,background:"#0F6E56",color:"#fff",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
function AptTooltip({apt,getType,getBeh,style}) {
  const typ=getType(apt.type), beh=getBeh(apt.behandler);
  return (
    <div style={{position:"absolute",zIndex:20,background:"#2C2C2E",color:"#e8e8f0",borderRadius:12,padding:"8px 11px",fontSize:11,minWidth:180,maxWidth:240,pointerEvents:"none",boxShadow:"0 4px 16px rgba(0,0,0,.25)",...style}}>
      <div style={{fontWeight:600,marginBottom:3}}>{apt.patient}</div>
      <div style={{color:"var(--mf-muted)",fontSize:10,marginBottom:2}}>Geb. {apt.dob}</div>
      {apt.allergy&&<div style={{color:"rgba(220,38,38,0.45)",fontSize:10,fontWeight:600,marginBottom:2}}>⚠ Allergie: {apt.allergy}</div>}
      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}>
        <span style={{width:6,height:6,borderRadius:"50%",background:typ.color,display:"inline-block"}}/>
        <span style={{color:"var(--mf-border2)"}}>{typ.label}</span>
      </div>
      <div style={{color:"var(--mf-border2)"}}>{apt.time} · {apt.dur} Min. · {beh.label}</div>
      {apt.room&&<div style={{color:"var(--mf-border2)"}}>{apt.room}</div>}
      {apt.anliegen&&<div style={{color:"var(--mf-border2)",marginTop:2}}>{apt.anliegen}</div>}
    </div>
  );
}

// ─── APT MODAL ────────────────────────────────────────────────────────────────
function AptModal({apt,types,behandler,onSave,onDelete,onClose,onOpenAkte,onCancelled}) {
  const today=new Date();
  const [form,setForm]=useState({
    patient:apt.patient||"",dob:apt.dob||"",behandler:apt.behandler||behandler[0].id,
    type:apt.type||types[0]?.id||"",anliegen:apt.anliegen||"",
    date:apt.date||dateKey(today),time:apt.time||"08:00",
    dur:apt.dur||30,kv:apt.kv||"Kasse",src:apt.src||"Vor Ort",
    notes:apt.notes||"",room:apt.room||"",
    status:apt.status||"gebucht",cancelReason:apt.cancelReason||"",
  });
  const [patQ,setPatQ]=useState(apt.patient||"");
  const [sugg,setSugg]=useState([]); const [showSugg,setShowSugg]=useState(false);
  const [showCancel,setShowCancel]=useState(false);
  const knownPatients=useMemo(getKnownPatients,[]);
  const u=(k,v)=>setForm(p=>({...p,[k]:v}));
  const isNew=!apt.id; const typ=types.find(t=>t.id===form.type);

  const handlePatInput=val=>{
    setPatQ(val); u("patient",val);
    if(val.length>=1){const q=val.toLowerCase();const f=knownPatients.filter(p=>p.name.toLowerCase().includes(q)).slice(0,8);setSugg(f);setShowSugg(f.length>0);}
    else setShowSugg(false);
  };
  const selectPat=p=>{setPatQ(p.name);setForm(f=>({...f,patient:p.name,dob:p.dob,kv:p.kv,allergy:p.allergy||""}));setShowSugg(false);};

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:60,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:16,width:460,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.2)",fontFamily:"system-ui,sans-serif"}}>
        <div style={{height:5,borderRadius:"16px 16px 0 0",background:typ?typ.color:"#0F6E56"}}/>
        <div style={{padding:"14px 20px 0",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <div style={{fontSize:15,fontWeight:700}}>{isNew?"Neuer Termin":"Termin bearbeiten"}</div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",border:"none",background:"var(--mf-bg-alt)",color:"var(--mf-muted)",cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>×</button>
        </div>
        <div style={{padding:"0 20px 20px",display:"flex",flexDirection:"column",gap:13}}>
          {/* Patient autocomplete */}
          <div>
            <label style={ml}>Patient</label>
            <div style={{position:"relative"}}>
              <input value={patQ} onChange={e=>handlePatInput(e.target.value)} onBlur={()=>setTimeout(()=>setShowSugg(false),150)} onFocus={()=>patQ.length>=1&&sugg.length&&setShowSugg(true)} placeholder="Name suchen oder neu eingeben…" style={mi} autoComplete="off"/>
              {showSugg&&<div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,.1)",zIndex:10,maxHeight:180,overflowY:"auto",marginTop:3}}>
                {sugg.map((p,i)=>(
                  <div key={i} onMouseDown={()=>selectPat(p)} style={{padding:"8px 12px",cursor:"pointer",borderBottom:"0.5px solid var(--mf-border)",display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="var(--mf-bg)"}>
                    <div>
                      <div style={{fontSize:12,fontWeight:600}}>{p.name}</div>
                      <div style={{fontSize:10,color:"var(--mf-muted)"}}>Geb. {p.dob}{p.allergy&&<span style={{color:"var(--mf-neg)",marginLeft:6}}>⚠ {p.allergy}</span>}</div>
                    </div>
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:"var(--mf-bg-alt)",color:"var(--mf-muted)"}}>{p.kv}</span>
                  </div>
                ))}
              </div>}
            </div>
          </div>
          {/* DOB + KV */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"end"}}>
            <div><label style={ml}>Geburtsdatum</label><DobInput value={form.dob} onChange={v=>u("dob",v)} style={mi}/></div>
            <Select label="Krankenversicherung" value={form.kv} onChange={v=>u("kv",v)} options={["Kasse","AOK","TK","BKK","Privat","Sonstiges"]} style={{marginBottom:0}}/>
          </div>
          {/* Type + Behandler */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Select label="Termintyp" value={form.type} onChange={v=>u("type",v)} options={types.map(t=>({value:t.id,label:t.label}))} style={{marginBottom:0}}/>
            <Select label="Behandler" value={form.behandler} onChange={v=>u("behandler",v)} options={behandler.map(b=>({value:b.id,label:b.name}))} style={{marginBottom:0}}/>
          </div>
          {/* Date + Time + Dur */}
          <div style={{display:"grid",gridTemplateColumns:"auto auto auto",gap:12,alignItems:"end"}}>
            <div><label style={ml}>Datum</label><DateInput value={form.date} onChange={v=>u("date",v)} style={mi} today={today}/></div>
            <div><label style={ml}>Uhrzeit</label><TimeInput value={form.time} onChange={v=>u("time",v)} style={mi}/></div>
            <div><label style={ml}>Dauer (Min.)</label><input type="number" min={5} max={480} step={5} value={form.dur} onChange={e=>u("dur",+e.target.value)} style={mi}/></div>
          </div>
          {/* Anliegen + Room */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"end"}}>
            <div><label style={ml}>Anliegen</label><input value={form.anliegen} onChange={e=>u("anliegen",e.target.value)} placeholder="Behandlungsgrund" style={mi}/></div>
            <Select label="Zimmer" value={form.room} onChange={v=>u("room",v)} options={ROOMS.map(r=>({value:r==="Kein Zimmer"?"":r,label:r}))} style={{marginBottom:0}}/>
          </div>
          {/* Source */}
          <div>
            <label style={ml}>Gebucht via</label>
            <div style={{display:"flex",gap:8}}>
              {[["Vor Ort",Picto.Pin],["Telefonisch",Picto.Phone]].map(([s,Ic])=>(
                <button key={s} onClick={()=>u("src",s)} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"9px 0",borderRadius:12,border:`1.5px solid ${form.src===s?"var(--mf-primary)":"var(--mf-border)"}`,background:form.src===s?"rgba(53,201,155,0.14)":"var(--mf-bg-alt)",color:form.src===s?"var(--mf-primary)":"var(--mf-muted)",fontWeight:form.src===s?600:400,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                  <Ic size={15} stroke={form.src===s?"var(--mf-primary)":"var(--mf-muted)"}/>{s}
                </button>
              ))}
            </div>
          </div>
          {/* Notes */}
          <div><label style={ml}>Notizen</label><textarea value={form.notes} onChange={e=>u("notes",e.target.value)} rows={2} placeholder="Interne Notizen…" style={{...mi,resize:"none"}}/></div>
          {/* Cancel reason (when editing existing) */}
          {!isNew&&form.status==="abgesagt"&&<div style={{padding:"10px 12px",background:"rgba(220,38,38,0.10)",border:"1px solid #fca5a5",borderRadius:12,fontSize:11,color:"var(--mf-neg)"}}><strong>Abgesagt:</strong> {form.cancelReason||"Kein Grund angegeben"}</div>}
          {/* Actions */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:6,borderTop:"0.5px solid var(--mf-border)"}}>
            <div style={{display:"flex",gap:7}}>
              {!isNew&&<button onClick={()=>{onDelete(apt.id);onClose();}} style={{fontSize:11,padding:"7px 13px",borderRadius:12,border:"1.5px solid #fca5a5",background:"rgba(220,38,38,0.10)",color:"var(--mf-neg)",cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>Löschen</button>}
              {!isNew&&<button onClick={()=>{onOpenAkte(apt);onClose();}} style={{fontSize:11,padding:"7px 13px",borderRadius:12,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>Akte</button>}
              {!isNew&&<button onClick={()=>setShowCancel(true)} style={{fontSize:11,padding:"7px 13px",borderRadius:12,border:"1.5px solid #fcd34d",background:"#fffbeb",color:"#C77D1A",cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>Absagen</button>}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={onClose} style={{fontSize:12,padding:"8px 18px",borderRadius:12,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>Abbrechen</button>
              <button onClick={()=>{onSave({...form,id:apt.id||Date.now(),allergy:form.allergy||"",waitSince:form.status==="wartend"?Date.now():null});onClose();}} style={{fontSize:12,padding:"8px 22px",borderRadius:12,background:"#0F6E56",color:"#fff",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Speichern</button>
            </div>
          </div>
        </div>
      </div>
      {/* Cancel reason sub-modal */}
      {showCancel&&<CancelReasonModal onConfirm={reason=>{onSave({...form,id:apt.id,status:"abgesagt",cancelReason:reason,allergy:form.allergy||""});onClose();if(onCancelled)onCancelled({date:form.date,time:form.time,dur:form.dur,behandler:form.behandler});}} onClose={()=>setShowCancel(false)}/>}
    </div>
  );
}

const ml={fontSize:11,fontWeight:600,color:"var(--mf-muted)",display:"block",marginBottom:5,letterSpacing:".2px"};
// an die ui.js-Formsprache angeglichen (13px-Radius, 1.5px-Border)
const mi={width:"100%",fontSize:12.5,padding:"10px 13px",borderRadius:13,border:"1.5px solid var(--mf-border2)",fontFamily:"inherit",background:"var(--mf-bg-alt)",color:"var(--mf-text)",outline:"none",boxSizing:"border-box"};

// ─── LÜCKEN-ASSISTENT ─────────────────────────────────────────────────────────
// Nach einer Absage: Kandidaten vorschlagen, um den frei gewordenen Slot zu
// füllen — überfällige Recalls zuerst, dann lange nicht gesehene Patienten.
const daysSinceDe=(s)=>{try{const[dd,mm,yy]=s.split(".");const dt=new Date(2000+ +yy,+mm-1,+dd);return isNaN(dt)?null:Math.round((Date.now()-dt.getTime())/86400000);}catch{return null;}};

function GapAssist({gap,behandler,onBook,onClose}) {
  const candidates=useMemo(()=>{
    const out=[];const seen=new Set();
    loadRecallsCache().filter(r=>r.status==="offen").sort((a,b)=>(b.tage||0)-(a.tage||0)).slice(0,6).forEach(r=>{
      seen.add(r.name);out.push({name:r.name,dob:r.dob||"",reason:`PZR überfällig${r.tage?` seit ${r.tage} Tagen`:""}`,urgent:(r.tage||0)>60});
    });
    if(out.length<6){
      loadPatientsCache().map(p=>({p,days:daysSinceDe(p.last||"")}))
        .filter(x=>x.days!=null&&x.days>365&&!seen.has(`${x.p.nachname}, ${x.p.vorname}`))
        .sort((a,b)=>b.days-a.days).slice(0,6-out.length)
        .forEach(x=>out.push({name:`${x.p.nachname}, ${x.p.vorname}`,dob:x.p.dob||"",reason:`Letzter Besuch vor ${Math.round(x.days/30)} Monaten`,urgent:false}));
    }
    return out;
  },[]);
  const behName=behandler.find(b=>b.id===gap.behandler)?.name||"";
  const dateDe=(()=>{try{const[y,m,d]=gap.date.split("-");return `${d}.${m}.${y}`;}catch{return gap.date;}})();
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:70,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:16,width:440,maxWidth:"94vw",maxHeight:"86vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.25)",padding:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <Picto.Calendar size={18}/>
          <div style={{fontSize:14,fontWeight:700}}>Lücke füllen?</div>
        </div>
        <div style={{fontSize:11.5,color:"var(--mf-muted)",marginBottom:12}}>
          {dateDe} · {gap.time} Uhr · {gap.dur} Min.{behName?` · ${behName}`:""} ist jetzt frei. Vorschläge aus Recall &amp; Reaktivierung:
        </div>
        {candidates.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",padding:"10px 0"}}>Keine Kandidaten — keine überfälligen Recalls oder inaktiven Patienten. 👍</div>}
        {candidates.map((cd,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cd.name}</div>
              <div style={{fontSize:10.5,color:cd.urgent?"var(--mf-neg)":"var(--mf-muted)"}}>{cd.reason}</div>
            </div>
            <button onClick={()=>onBook(cd)} style={{fontSize:11,padding:"6px 13px",borderRadius:10,border:"none",background:"var(--mf-primary)",color:"#fff",cursor:"pointer",fontFamily:"inherit",fontWeight:600,flexShrink:0}}>Einbuchen</button>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:14}}>
          <button onClick={onClose} style={{fontSize:12,padding:"8px 18px",borderRadius:12,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit",fontWeight:500,color:"var(--mf-text)"}}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

// ─── CANCEL REASON MODAL ──────────────────────────────────────────────────────
function CancelReasonModal({onConfirm,onClose}) {
  const [reason,setReason]=useState("");
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:80,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:14,padding:20,width:360,boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>Absagegrund</div>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3} placeholder="z.B. Patient hat kurzfristig abgesagt, beruflicher Termin…" style={{width:"100%",fontSize:12,padding:"9px 11px",borderRadius:12,border:"1.5px solid var(--mf-border)",fontFamily:"inherit",resize:"none",boxSizing:"border-box"}}/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
          <button onClick={onClose} style={{fontSize:12,padding:"7px 16px",borderRadius:12,border:"1.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",cursor:"pointer",fontFamily:"inherit"}}>Abbrechen</button>
          <button onClick={()=>{if(!reason.trim())return;onConfirm(reason);}} disabled={!reason.trim()} style={{fontSize:12,padding:"7px 16px",borderRadius:12,background:reason.trim()?"var(--mf-neg)":"var(--mf-border)",color:reason.trim()?"#fff":"var(--mf-muted)",border:"none",cursor:reason.trim()?"pointer":"default",fontFamily:"inherit",fontWeight:600}}>Termin absagen</button>
        </div>
      </div>
    </div>
  );
}

// ─── CANCELLATION PANEL ───────────────────────────────────────────────────────
function CancellationPanel({apts,onClose,onOpenAkte}) {
  const cancelled=apts.filter(a=>a.status==="abgesagt");
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:60,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:14,padding:18,width:460,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:700}}>Absagen ({cancelled.length})</div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",border:"none",background:"var(--mf-bg-alt)",cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>×</button>
        </div>
        {cancelled.length===0&&<div style={{fontSize:12,color:"var(--mf-muted)",textAlign:"center",padding:24}}>Keine Absagen</div>}
        {cancelled.map(a=>(
          <div key={a.id} style={{background:"rgba(220,38,38,0.10)",border:"0.5px solid #fca5a5",borderRadius:10,padding:"10px 13px",marginBottom:8}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <div style={{fontSize:13,fontWeight:600}}>{a.patient}</div>
              <div style={{fontSize:10,color:"var(--mf-muted)"}}>Geb. {a.dob}</div>
            </div>
            <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:3}}>{a.date} · {a.time} · {a.anliegen}</div>
            {a.cancelReason&&<div style={{fontSize:11,color:"var(--mf-neg)",fontWeight:500,marginBottom:6}}>Grund: {a.cancelReason}</div>}
            <button onClick={()=>{onOpenAkte(a);onClose();}} style={{fontSize:11,padding:"4px 11px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg)",cursor:"pointer",fontFamily:"inherit"}}>Akte öffnen →</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── AKTE PANEL ───────────────────────────────────────────────────────────────
function AktePanel({apt,onClose,onSaveNotes}) {
  const [note,setNote]=useState("");
  const [notes,setNotes]=useState(()=>{
    try{const s=localStorage.getItem(`dida_notes_${apt.id}`);if(s)return JSON.parse(s);}catch{}
    return apt.notes?[{date:apt.date,text:apt.notes}]:[];
  });
  const add=()=>{
    if(!note.trim())return;
    const updated=[{date:new Date().toLocaleDateString("de-DE"),text:note},...notes];
    setNotes(updated);
    setNote("");
    try{localStorage.setItem(`dida_notes_${apt.id}`,JSON.stringify(updated));}catch{}
    if(onSaveNotes)onSaveNotes(apt.id,updated);
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:70,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",borderRadius:14,padding:18,width:400,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
          <div style={{width:38,height:38,borderRadius:"50%",background:"#E1F5EE",color:"#085041",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700}}>{apt.patient.split(/[\s,]+/).filter(Boolean).map(w=>w[0]).join("").slice(0,2).toUpperCase()}</div>
          <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700}}>{apt.patient}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>Geb. {apt.dob} · {apt.kv}</div></div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",border:"none",background:"var(--mf-bg-alt)",cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>×</button>
        </div>
        {apt.allergy&&<div style={{padding:"7px 10px",background:"rgba(220,38,38,0.10)",border:"1px solid #fca5a5",borderRadius:7,fontSize:11,color:"var(--mf-neg)",fontWeight:600,marginBottom:10}}>⚠ Allergie: {apt.allergy}</div>}
        <div style={{height:"0.5px",background:"var(--mf-border)",margin:"8px 0"}}/>
        <div style={{fontSize:10,fontWeight:600,color:"var(--mf-muted)",textTransform:"uppercase",letterSpacing:".4px",marginBottom:5}}>Letzter Termin</div>
        <div style={{fontSize:11,marginBottom:10}}>{apt.date} · {apt.time} · {apt.anliegen}{apt.room&&` · ${apt.room}`}</div>
        <div style={{display:"flex",gap:6,marginBottom:8}}>
          <input value={note} onChange={e=>setNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="Notiz hinzufügen…" style={{flex:1,fontSize:12,padding:"7px 9px",borderRadius:7,border:"1.5px solid var(--mf-border)",fontFamily:"inherit"}}/>
          <button onClick={add} style={{fontSize:12,padding:"7px 13px",borderRadius:7,background:"#0F6E56",color:"#fff",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>+</button>
        </div>
        {notes.map((n,i)=>(
          <div key={i} style={{padding:"7px 9px",background:"var(--mf-bg-alt)",borderRadius:7,marginBottom:5}}>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:2}}>{n.date}</div>
            <div style={{fontSize:11}}>{n.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── WAITING BAR ──────────────────────────────────────────────────────────────
function WaitingRoomBar({apts}) {
  const waiting=apts.filter(a=>a.status==="wartend"&&a.waitSince);
  if(!waiting.length) return null;
  const now=Date.now();
  const avg=Math.round(waiting.reduce((s,a)=>s+(now-a.waitSince),0)/waiting.length/60000);
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",background:"rgba(245,158,11,0.14)",border:"0.5px solid #FCD34D",borderRadius:12,flexWrap:"wrap"}}>
      <span style={{fontSize:11,fontWeight:700,color:"#C77D1A"}}>Wartezimmer</span>
      {waiting.map(a=>(
        <div key={a.id} style={{display:"flex",alignItems:"center",gap:5,background:"var(--mf-bg)",borderRadius:20,padding:"2px 10px",border:"0.5px solid #FCD34D"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"rgba(245,158,11,0.55)"}}/>
          <span style={{fontSize:11,fontWeight:500}}>{a.patient.split(",")[0]||a.patient.split(" ").pop()}</span>
          <span style={{fontSize:10,color:"#C77D1A"}}>{fmtWait(now-a.waitSince)}</span>
        </div>
      ))}
      <span style={{fontSize:10,color:"var(--mf-muted)",marginLeft:"auto"}}>Ø <strong style={{color:"#C77D1A"}}>{avg} Min.</strong></span>
    </div>
  );
}

function relativeDayLabel(date,today) {
  const diff=Math.round((new Date(dateKey(date))-new Date(dateKey(today)))/86400000);
  if(diff===0)  return {label:"Heute",      color:"#0F6E56",bg:"#E1F5EE"};
  if(diff===1)  return {label:"Morgen",     color:"#0F6E56",bg:"#E1F5EE"};
  if(diff===-1) return {label:"Gestern",    color:"var(--mf-muted)",bg:"var(--mf-bg-alt)"};
  if(diff===2)  return {label:"Übermorgen", color:"#0F6E56",bg:"#E1F5EE"};
  if(diff===-2) return {label:"Vorgestern", color:"var(--mf-muted)",bg:"var(--mf-bg-alt)"};
  return null;
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export const DEFAULT_DAY_CONFIG = {
  1:{start:8,end:18,closed:false}, // Mo
  2:{start:8,end:18,closed:false}, // Di
  3:{start:8,end:18,closed:false}, // Mi
  4:{start:8,end:18,closed:false}, // Do
  5:{start:8,end:13,closed:false}, // Fr
  6:{start:0,end:0, closed:true},  // Sa
  0:{start:0,end:0, closed:true},  // So
};

export default function TermineScreen({ presetPatient, onPresetConsumed }) {
  // Behandler aus den Praxis-Einstellungen — als State, damit der async
  // geladene Settings-Cache den ersten Render korrigiert (Race-Fix)
  const [behandlerList, setBehandlerList] = useState(getBehandler);
  useEffect(()=>{ loadSettings().then(s=>{ if(s.behandler.length) setBehandlerList(s.behandler); }).catch(()=>{}); },[]);
  const today=new Date();
  const [view,setView]=useState("tag");
  const [cur,setCur]=useState(today);
  const [apts,setApts]=useState(()=>{ const cached=loadAptsCache(); return cached.length?cached:(demoFallbacks()?INITIAL_APTS:[]); });
  useEffect(()=>{ let on=true; listApts().then(r=>{ if(on&&(r.length||!demoFallbacks())) setApts(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  const [types,setTypes]=useState(()=>{try{const s=localStorage.getItem("dida_types");return s?JSON.parse(s):DEFAULT_TYPES;}catch{return DEFAULT_TYPES;}});
  const [dayCfg,setDayCfg]=useState(()=>{try{const s=localStorage.getItem("dida_daycfg");return s?JSON.parse(s):DEFAULT_DAY_CONFIG;}catch{return DEFAULT_DAY_CONFIG;}});
  const [filterBeh,setFilterBeh]=useState("");
  // Mobil: immer genau EIN Behandler (Spaltenbreite!) und Tagesansicht
  const [isMobileTS,setIsMobileTS]=useState(()=>typeof window!=="undefined"&&window.innerWidth<640);
  useEffect(()=>{ const f=()=>setIsMobileTS(window.innerWidth<640); window.addEventListener("resize",f); return ()=>window.removeEventListener("resize",f); },[]);
  useEffect(()=>{ if(isMobileTS&&!filterBeh&&behandlerList.length) setFilterBeh(behandlerList[0].id); },[isMobileTS,filterBeh,behandlerList]);
  useEffect(()=>{ if(isMobileTS&&view!=="tag") setView("tag"); },[isMobileTS,view]);
  // Verwaiste Behandler-IDs erst normalisieren, wenn die echte Liste da ist
  const normalizedRef = useRef(false);
  useEffect(()=>{
    if(normalizedRef.current || !behandlerList.length) return;
    normalizedRef.current = true;
    setApts(prev=>{
      const known = new Set(behandlerList.map(b=>b.id));
      if(!prev.some(a=>!known.has(a.behandler))) return prev;
      const next = prev.map(a=>known.has(a.behandler)?a:{...a,behandler:behandlerList[0].id});
      saveAllApts(next).catch(()=>{}); // Zuordnung auch auf dem Server reparieren
      return next;
    });
  },[behandlerList]);
  const [showSettings,setShowSettings]=useState(false);
  const [showCancelled,setShowCancelled]=useState(false);
  const [modalApt,setModalApt]=useState(null);
  const [gapInfo,setGapInfo]=useState(null); // Lücken-Assistent nach Absage
  const [akteApt,setAkteApt]=useState(null);
  const [dragApt,setDragApt]=useState(null);
  const [dragOver,setDragOver]=useState(null);
  const [showMiniCal,setShowMiniCal]=useState(false);
  const [tooltip,setTooltip]=useState(null); // {apt, x, y}
  const [,forceUpdate]=useState(0);

  useEffect(()=>{ const t=setInterval(()=>forceUpdate(n=>n+1),30000); return()=>clearInterval(t); },[]);
  useEffect(()=>{
    if(!presetPatient) return;
    setModalApt({id:null,date:dateKey(today),time:"09:00",behandler:behandlerList[0]?.id||"",
      patient:presetPatient.patient||"",dob:presetPatient.dob||"",allergy:presetPatient.allergy||"",
      type:DEFAULT_TYPES[0]?.id||"",anliegen:"",dur:30,kv:presetPatient.kv||"Kasse",
      src:"Patienten-Akte",notes:"",room:"",status:"gebucht",waitSince:null});
    if(onPresetConsumed) onPresetConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[presetPatient]);
  useEffect(()=>{ if(serverActive()) return; try{localStorage.setItem("dida_apts",JSON.stringify(apts));}catch{} },[apts]); // LIVE spiegelt der Store
  useEffect(()=>{try{localStorage.setItem("dida_types",JSON.stringify(types));}catch{}},[types]);
  useEffect(()=>{try{localStorage.setItem("dida_daycfg",JSON.stringify(dayCfg));}catch{}},[dayCfg]);

  const weekStart=startOfWeek(cur);
  const weekDays=Array.from({length:7},(_,i)=>addDays(weekStart,i));
  const navigate=n=>{ if(view==="tag") setCur(d=>addDays(d,n)); else setCur(d=>addDays(d,n*7)); };
  const goToday=()=>setCur(today);
  const saveApt=apt=>{
    const full = (apt.id&&apts.find(a=>a.id===apt.id)) ? apt : {...apt, id: apt.id||Date.now()};
    setApts(prev=>prev.find(a=>a.id===full.id)?prev.map(a=>a.id===full.id?full:a):[...prev,full]); // optimistisch
    storeUpsertApt(full).then(setApts).catch(()=>toast("Termin nicht gespeichert — Verbindung prüfen.","r"));
  };
  const deleteApt=id=>{
    setApts(prev=>prev.filter(a=>a.id!==id)); // optimistisch
    storeDeleteApt(id).then(setApts).catch(()=>{ toast("Löschen nicht gespeichert — Verbindung prüfen.","r"); listApts().then(setApts).catch(()=>{}); });
  };
  const getType=id=>types.find(t=>t.id===id)||{color:"#888",bg:"#eee",label:"?"};
  const getBeh=id=>behandlerList.find(b=>b.id===id)||{name:id,color:"#888"};
  const onDragStart=(e,apt)=>{setDragApt(apt);e.dataTransfer.effectAllowed="move";};
  const onDragOver=(e,date,time,behId)=>{e.preventDefault();setDragOver({date:dateKey(date),time,behandler:behId});};
  const onDrop=(e,date,time,behId)=>{
    e.preventDefault(); if(!dragApt) return;
    const moved={...dragApt,date:dateKey(date),time,behandler:behId||dragApt.behandler};
    setApts(prev=>prev.map(a=>a.id===moved.id?moved:a)); // optimistisch
    storeUpsertApt(moved).then(setApts).catch(()=>toast("Verschieben nicht gespeichert — Verbindung prüfen.","r"));
    setDragApt(null); setDragOver(null);
  };
  const onDragEnd=()=>{setDragApt(null);setDragOver(null);};
  const openNewApt=(date,time,behId)=>setModalApt({id:null,date:dateKey(date),time,behandler:behId||behandlerList[0]?.id||"",patient:"",dob:"",allergy:"",type:types[0]?.id||"",anliegen:"",dur:30,kv:"Kasse",src:"Vor Ort",notes:"",room:"",status:"gebucht",waitSince:null});

  const behs=filterBeh?behandlerList.filter(b=>b.id===filterBeh):behandlerList;
  const cancelledCount=apts.filter(a=>a.status==="abgesagt").length;
  const getWeekNum=d=>{const jan4=new Date(d.getFullYear(),0,4);const dayOfWeek=((jan4.getDay()+6)%7);const weekStart=new Date(jan4);weekStart.setDate(jan4.getDate()-dayOfWeek);return Math.floor((d-weekStart)/604800000)+1;};
  const headerDate=view==="tag"
    ?`${DAYS_DE[cur.getDay()]}, ${cur.getDate()}. ${MONTHS_DE[cur.getMonth()]} ${cur.getFullYear()}`
    :`KW ${getWeekNum(weekStart)} · ${weekStart.getDate()}. ${MONTHS_DE[weekStart.getMonth()].slice(0,3)} – ${addDays(weekStart,6).getDate()}. ${MONTHS_DE[addDays(weekStart,6).getMonth()].slice(0,3)}. ${weekStart.getFullYear()}`;
  const relLabel=view==="tag"?relativeDayLabel(cur,today):null;
  const curDayCfg=dayCfg[cur.getDay()]||{start:7,end:20,closed:false};
  const slots=curDayCfg.closed?[]:buildSlots(curDayCfg.start,curDayCfg.end);

  if(!behandlerList.length) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px",gap:10,textAlign:"center"}}>
      <i className="ti ti-users-plus" style={{fontSize:34,color:"var(--mf-border2)"}}/>
      <div style={{fontSize:14,fontWeight:600}}>Noch keine Behandler angelegt</div>
      <div style={{fontSize:12,color:"var(--mf-muted)",maxWidth:380,lineHeight:1.5}}>Der Terminplan zeigt eine Spalte pro Behandler. Lege zuerst dein Team an: <strong>Benutzermenü (unten links) → Praxis &amp; Behandler</strong>.</div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,fontFamily:"system-ui,sans-serif",height:"100%",position:"relative"}}>
      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
        <button onClick={()=>navigate(-1)} style={btnS}>←</button>
        <div style={{position:"relative"}}>
          <button onClick={()=>setShowMiniCal(v=>!v)} style={{...btnS,fontWeight:600,fontSize:12,minWidth:220,textAlign:"left"}}>{headerDate}</button>
          {showMiniCal&&<><div onClick={()=>setShowMiniCal(false)} style={{position:"fixed",inset:0,zIndex:49}}/><MiniCalendar current={cur} today={today} onSelect={d=>{setCur(d);setView("tag");setShowMiniCal(false);}} onClose={()=>setShowMiniCal(false)}/></>}
        </div>
        <button onClick={()=>navigate(1)} style={btnS}>→</button>
        {relLabel
          ?<button onClick={goToday} style={{...btnS,background:relLabel.bg,color:relLabel.color,border:`0.5px solid ${relLabel.color}44`,fontWeight:600}}>{relLabel.label}</button>
          :<button onClick={goToday} style={btnS}>Heute</button>}
        <div style={{display:"flex",border:"0.5px solid var(--mf-border2)",borderRadius:6,overflow:"hidden"}}>
          {[["tag","Tag"],["woche","Woche"]].map(([k,l])=>(
            <button key={k} onClick={()=>setView(k)} style={{fontSize:11,padding:"4px 12px",border:"none",background:view===k?"#0F6E56":"transparent",color:view===k?"#fff":"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:3}}>
          {!isMobileTS&&<button onClick={()=>setFilterBeh("")} style={{...btnS,background:!filterBeh?"var(--mf-text)":"transparent",color:!filterBeh?"#fff":"var(--mf-muted)",fontSize:10,padding:"3px 8px"}}>Alle</button>}
          {behandlerList.map(b=>(
            <button key={b.id} onClick={()=>setFilterBeh(filterBeh===b.id?"":b.id)} style={{fontSize:10,padding:"3px 9px",borderRadius:20,border:`0.5px solid ${b.color}55`,background:filterBeh===b.id?b.color:"transparent",color:filterBeh===b.id?"#fff":b.color,cursor:"pointer",fontFamily:"inherit"}}>
              {b.name.split(" ").pop()}
            </button>
          ))}
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:5,alignItems:"center"}}>
          <button onClick={()=>setShowCancelled(true)} style={{...btnS,background:"rgba(220,38,38,0.10)",color:"var(--mf-neg)",border:"0.5px solid #fca5a5",display:"flex",alignItems:"center",gap:5}}>
            Absagen<span style={{background:"var(--mf-neg)",color:"#fff",borderRadius:10,padding:"0 5px",fontSize:10,fontWeight:700}}>{cancelledCount}</span>
          </button>
          <button onClick={()=>setShowSettings(true)} style={btnS}>⚙ Einstellungen</button>
          <button onClick={()=>openNewApt(cur,"09:00",behs[0]?.id)} style={{...btnS,background:"#0F6E56",color:"#fff",border:"none"}}>+ Termin</button>
        </div>
      </div>

      {/* LEGEND */}
      <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
        {types.map(t=>(
          <div key={t.id} style={{display:"flex",alignItems:"center",gap:4,fontSize:10,padding:"2px 8px",borderRadius:20,background:t.bg||t.color+"22",color:t.color,border:`0.5px solid ${t.color}44`}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:t.color,display:"inline-block"}}/>{t.label}
          </div>
        ))}
        {curDayCfg.closed&&<span style={{fontSize:10,color:"var(--mf-neg)",fontWeight:600,padding:"2px 10px",background:"rgba(220,38,38,0.10)",borderRadius:20}}>⚠ Heute geschlossen</span>}
      </div>

      {/* WAITING ROOM */}
      <WaitingRoomBar apts={apts}/>

      {/* CALENDAR */}
      <div style={{flex:1,overflowY:"auto",overflowX:"auto",background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderRadius:10,position:"relative"}}>
        {curDayCfg.closed&&view==="tag"
          ?<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:200,gap:8,color:"var(--mf-muted)"}}>
            <div style={{fontSize:32}}>🔒</div>
            <div style={{fontSize:14,fontWeight:600}}>Praxis heute geschlossen</div>
            <div style={{fontSize:12}}>Öffnungszeiten in den Einstellungen anpassen</div>
          </div>
          :view==="tag"
            ?<DayView date={cur} apts={apts} behs={behs} filterBeh={filterBeh} getType={getType} getBeh={getBeh} slots={slots} startH={curDayCfg.start} onOpenApt={a=>setModalApt(a)} onNewApt={openNewApt} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd} dragOver={dragOver} dragApt={dragApt} onTooltip={setTooltip}/>
            :<WeekView weekDays={weekDays} apts={apts} behs={behs} filterBeh={filterBeh} getType={getType} getBeh={getBeh} dayCfg={dayCfg} onOpenApt={a=>setModalApt(a)} onNewApt={openNewApt} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd} dragOver={dragOver} dragApt={dragApt} today={today} onTooltip={setTooltip}/>
        }
        {/* Tooltip */}
        {tooltip&&<AptTooltip apt={tooltip.apt} getType={getType} getBeh={getBeh} style={{top:tooltip.y,left:tooltip.x,position:"absolute"}}/>}
      </div>

      {/* MODALS */}
      {showSettings&&<SettingsModal types={types} onSave={setTypes} onClose={()=>setShowSettings(false)} dayConfig={dayCfg} onSaveDayConfig={setDayCfg}/>}
      {showCancelled&&<CancellationPanel apts={apts} onClose={()=>setShowCancelled(false)} onOpenAkte={a=>{setShowCancelled(false);setAkteApt(a);}}/>}
      {modalApt&&<AptModal apt={modalApt} types={types} behandler={behandlerList.length?behandlerList:[{id:"",name:"— Behandler anlegen —",color:"#888"}]} onSave={saveApt} onDelete={deleteApt} onClose={()=>setModalApt(null)} onOpenAkte={a=>{setModalApt(null);setAkteApt(a);}} onCancelled={g=>setGapInfo(g)}/>}
      {gapInfo&&<GapAssist gap={gapInfo} behandler={behandlerList} onClose={()=>setGapInfo(null)}
        onBook={cd=>{
          const pzrType=types.find(t=>/pzr|prophylaxe/i.test(t.label||""))?.id||types[0]?.id||"";
          setModalApt({id:null,date:gapInfo.date,time:gapInfo.time,dur:gapInfo.dur||30,behandler:gapInfo.behandler||behandlerList[0]?.id||"",patient:cd.name,dob:cd.dob,allergy:"",type:pzrType,anliegen:"Recall / Reaktivierung",kv:"Kasse",src:"Telefonisch",notes:"Aus Lücken-Assistent eingebucht",room:"",status:"gebucht",waitSince:null});
          setGapInfo(null);
        }}/>}
      {akteApt&&<AktePanel apt={akteApt} onClose={()=>setAkteApt(null)} onSaveNotes={(id,notes)=>{
        const next=apts.map(a=>a.id===id?{...a,notes:notes[0]?.text||a.notes}:a);
        setApts(next);
        const hit=next.find(a=>a.id===id);
        if(hit) storeUpsertApt(hit).catch(()=>toast("Notiz nicht gespeichert — Verbindung prüfen.","r"));
      }}/>}
    </div>
  );
}

const btnS={fontSize:11,padding:"4px 12px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",cursor:"pointer",fontFamily:"inherit"};

// ─── DAY VIEW ─────────────────────────────────────────────────────────────────
function DayView({date,apts,behs,filterBeh,getType,getBeh,slots,startH,onOpenApt,onNewApt,onDragStart,onDragOver,onDrop,onDragEnd,dragOver,dragApt,onTooltip}) {
  const dk=dateKey(date);
  const dayApts=useMemo(()=>apts.filter(a=>a.date===dk&&(!filterBeh||a.behandler===filterBeh)),[apts,dk,filterBeh]);

  return (
    <div style={{display:"flex",minWidth:behs.length*190+52}}>
      {/* Time col */}
      <div style={{width:52,flexShrink:0,borderRight:"0.5px solid var(--mf-border)"}}>
        <div style={{height:40,borderBottom:"0.5px solid var(--mf-border)"}}/>
        {slots.map((s,i)=>(
          <div key={s} style={{height:SLOT_H,borderBottom:i%4===3?"0.5px solid var(--mf-border)":"0.5px solid var(--mf-border)",padding:"2px 6px 0 0",fontSize:9,color:"var(--mf-muted)",textAlign:"right"}}>{i%4===0?s:""}</div>
        ))}
      </div>
      {behs.map(beh=>{
        const behApts=dayApts.filter(a=>a.behandler===beh.id);
        // kein useMemo in .map() — Hook-Anzahl variiert mit Behandler-Filter (rules-of-hooks)
        const layout=computeLayout(behApts);
        return (
          <div key={beh.id} style={{flex:1,minWidth:160,borderRight:"0.5px solid var(--mf-border)",position:"relative"}}>
            <div style={{height:40,borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:"var(--mf-bg-alt)",position:"sticky",top:0,zIndex:2}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:beh.color}}/>
              <span style={{fontSize:11,fontWeight:600}}>{beh.name}</span>
              <span style={{fontSize:10,color:"var(--mf-muted)"}}>({behApts.filter(a=>a.status!=="abgesagt").length})</span>
            </div>
            <div
              style={{position:"relative",height:slots.length*SLOT_H}}
              onDragOver={e=>{
                e.preventDefault();
                const rect=e.currentTarget.getBoundingClientRect();
                const y=e.clientY-rect.top;
                const absMin=Math.round(y/SLOT_H)*SLOT_MINS+startH*60;
                const snapped=minsToTime(Math.max(startH*60,Math.min((startH+12)*60,absMin)));
                onDragOver(e,date,snapped,beh.id);
              }}
              onDrop={e=>{
                e.preventDefault();
                const rect=e.currentTarget.getBoundingClientRect();
                const y=e.clientY-rect.top;
                const absMin=Math.round(y/SLOT_H)*SLOT_MINS+startH*60;
                const snapped=minsToTime(Math.max(startH*60,Math.min((startH+12)*60,absMin)));
                onDrop(e,date,snapped,beh.id);
              }}
            >
              {/* Grid */}
              {slots.map((s,i)=>(
                <div key={s} style={{position:"absolute",top:i*SLOT_H,left:0,right:0,height:SLOT_H,borderBottom:i%4===3?"0.5px solid var(--mf-border)":"0.5px solid var(--mf-border)",pointerEvents:"none"}}/>
              ))}
              {/* Drag time label */}
              {dragOver?.date===dk&&dragOver?.behandler===beh.id&&(
                <div style={{position:"absolute",top:timeToTop(dragOver.time,startH)-11,left:4,pointerEvents:"none",zIndex:5}}>
                  <span style={{fontSize:10,fontWeight:700,color:"#0F6E56",background:"var(--mf-bg)",padding:"1px 5px",borderRadius:4,border:"1px solid #0F6E56",boxShadow:"0 1px 4px rgba(0,0,0,.12)"}}>{dragOver.time}</span>
                </div>
              )}
              {/* Click to book */}
              <div style={{position:"absolute",inset:0,zIndex:1}} onClick={e=>{
                const rect=e.currentTarget.getBoundingClientRect();
                const absMin=Math.round((e.clientY-rect.top)/SLOT_H)*SLOT_MINS+startH*60;
                onNewApt(date,minsToTime(Math.max(startH*60,absMin)),beh.id);
              }}/>
              {/* Appointments */}
              {behApts.map(apt=>{
                const typ=getType(apt.type);
                const top=timeToTop(apt.time,startH);
                const height=Math.max(durToH(apt.dur),22);
                const isCancelled=apt.status==="abgesagt";
                const isWaiting=apt.status==="wartend";
                const isDragging=dragApt?.id===apt.id;
                const info=layout[apt.id]||{col:0,totalCols:1};
                const colW=100/info.totalCols;
                const waitExt=isWaiting&&apt.waitSince?16:0;
                return (
                  <div
                    key={apt.id}
                    draggable
                    onDragStart={e=>onDragStart(e,apt)}
                    onDragEnd={onDragEnd}
                    onClick={e=>{e.stopPropagation();onOpenApt(apt);}}
                    onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();onTooltip({apt,x:r.right+8,y:r.top});}}
                    onMouseLeave={()=>onTooltip(null)}
                    style={{
                      position:"absolute",
                      left:`${info.col*colW+0.4}%`,width:`${colW-0.8}%`,
                      top:top-waitExt,height:height+waitExt,
                      background:isCancelled?"var(--mf-bg-alt)":typ.bg||typ.color+"22",
                      border:`1.5px solid ${isCancelled?"var(--mf-border2)":typ.color}`,
                      borderLeft:`3px solid ${isCancelled?"var(--mf-border2)":typ.color}`,
                      borderRadius:10,padding:"2px 4px",cursor:"grab",
                      opacity:isDragging?.35:1,zIndex:4,overflow:"hidden",boxSizing:"border-box",
                    }}
                  >
                    {isWaiting&&apt.waitSince&&(
                      <div style={{position:"absolute",top:0,left:0,right:0,height:waitExt,borderBottom:`2px dashed ${typ.color}`,background:"var(--mf-bg)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:8,fontWeight:700,color:typ.color}}>{fmtWait(Date.now()-apt.waitSince)}</span>
                      </div>
                    )}
                    <div style={{marginTop:waitExt}}>
                      <div style={{fontSize:10,fontWeight:600,color:isCancelled?"var(--mf-muted)":typ.color,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {apt.allergy&&!isCancelled&&<span style={{color:"var(--mf-neg)",marginRight:3}}>⚠</span>}
                        {apt.patient}
                      </div>
                      {height>28&&<div style={{fontSize:9,color:"var(--mf-muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{apt.anliegen}</div>}
                      {height>42&&<div style={{fontSize:9,color:"var(--mf-muted)"}}>{apt.time} · {apt.dur}Min.{apt.room&&` · ${apt.room}`}</div>}
                      {isCancelled&&<div style={{fontSize:8,color:"var(--mf-neg)",fontWeight:700}}>ABGESAGT</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── WEEK VIEW ────────────────────────────────────────────────────────────────
function WeekView({weekDays,apts,behs,filterBeh,getType,getBeh,dayCfg,onOpenApt,onNewApt,onDragStart,onDragOver,onDrop,onDragEnd,dragOver,dragApt,today,onTooltip}) {
  // Use max range across all days for consistent grid
  const allCfgs=weekDays.map(d=>dayCfg[d.getDay()]||{start:7,end:20,closed:false});
  const globalStart=Math.min(...allCfgs.filter(c=>!c.closed).map(c=>c.start));
  const globalEnd=Math.max(...allCfgs.filter(c=>!c.closed).map(c=>c.end));
  const slots=useMemo(()=>buildSlots(globalStart,globalEnd),[globalStart,globalEnd]);

  return (
    <div style={{display:"flex",minWidth:560}}>
      <div style={{width:44,flexShrink:0,borderRight:"0.5px solid var(--mf-border)"}}>
        <div style={{height:48,borderBottom:"0.5px solid var(--mf-border)"}}/>
        {slots.map((s,i)=>(
          <div key={s} style={{height:SLOT_H,borderBottom:i%4===3?"0.5px solid var(--mf-border)":"0.5px solid var(--mf-border)",fontSize:9,color:"var(--mf-muted)",textAlign:"right",padding:"2px 4px 0 0"}}>{i%4===0?s:""}</div>
        ))}
      </div>
      {weekDays.map(day=>{
        const dk=dateKey(day);
        const dayApts=apts.filter(a=>a.date===dk&&(!filterBeh||a.behandler===filterBeh));
        // kein useMemo in .map() — rules-of-hooks
        const layout=computeLayout(dayApts);
        const isToday=dk===dateKey(today);
        const cfg=dayCfg[day.getDay()]||{start:7,end:20,closed:false};
        return (
          <div key={dk} style={{flex:1,borderRight:"0.5px solid var(--mf-border)",position:"relative",minWidth:70}}>
            <div style={{height:48,borderBottom:"0.5px solid var(--mf-border)",padding:"5px 3px",background:isToday?"#E1F5EE":"var(--mf-bg-alt)",position:"sticky",top:0,zIndex:2,textAlign:"center"}}>
              <div style={{fontSize:10,color:isToday?"#0F6E56":cfg.closed?"var(--mf-border2)":"var(--mf-muted)",fontWeight:500}}>{DAYS_DE[day.getDay()]}</div>
              <div style={{fontSize:15,fontWeight:700,color:isToday?"#0F6E56":cfg.closed?"var(--mf-border2)":"var(--mf-text)",lineHeight:1.2}}>{day.getDate()}</div>
              {cfg.closed?<div style={{fontSize:8,color:"var(--mf-border2)"}}>geschl.</div>:<div style={{fontSize:9,color:"var(--mf-muted)"}}>{dayApts.length}T</div>}
            </div>
            <div
              style={{position:"relative",height:slots.length*SLOT_H,background:cfg.closed?"repeating-linear-gradient(45deg,transparent,transparent 4px,var(--mf-bg-alt) 4px,var(--mf-bg-alt) 8px)":"transparent"}}
              onDragOver={e=>{e.preventDefault();const rect=e.currentTarget.getBoundingClientRect();const y=e.clientY-rect.top;const absMin=Math.round(y/SLOT_H)*SLOT_MINS+globalStart*60;const snapped=minsToTime(Math.max(globalStart*60,Math.min(globalEnd*60-SLOT_MINS,absMin)));onDragOver(e,day,snapped,dragApt?.behandler||"");}}
              onDrop={e=>{e.preventDefault();const rect=e.currentTarget.getBoundingClientRect();const y=e.clientY-rect.top;const absMin=Math.round(y/SLOT_H)*SLOT_MINS+globalStart*60;const snapped=minsToTime(Math.max(globalStart*60,Math.min(globalEnd*60-SLOT_MINS,absMin)));onDrop(e,day,snapped,dragApt?.behandler||"");}}
            >
              {slots.map((s,i)=>(
                <div key={s} style={{position:"absolute",top:i*SLOT_H,left:0,right:0,height:SLOT_H,borderBottom:i%4===3?"0.5px solid var(--mf-border)":"0.5px solid var(--mf-border)",pointerEvents:"none"}}/>
              ))}
              {!cfg.closed&&<div style={{position:"absolute",inset:0,zIndex:1}} onClick={e=>{const rect=e.currentTarget.getBoundingClientRect();const absMin=Math.round((e.clientY-rect.top)/SLOT_H)*SLOT_MINS+globalStart*60;onNewApt(day,minsToTime(absMin),filterBeh||behs[0]?.id||"dh");}}/>}
              {dragOver?.date===dk&&(
                <div style={{position:"absolute",top:timeToTop(dragOver.time,globalStart)-11,left:4,pointerEvents:"none",zIndex:5}}>
                  <span style={{fontSize:9,fontWeight:700,color:"#0F6E56",background:"var(--mf-bg)",padding:"1px 4px",borderRadius:4,border:"1px solid #0F6E56"}}>{dragOver.time}</span>
                </div>
              )}
              {dayApts.map(apt=>{
                const typ=getType(apt.type);
                const top=timeToTop(apt.time,globalStart);
                const height=Math.max(durToH(apt.dur),18);
                const isCancelled=apt.status==="abgesagt";
                const isDragging=dragApt?.id===apt.id;
                const info=layout[apt.id]||{col:0,totalCols:1};
                const colW=100/info.totalCols;
                return (
                  <div
                    key={apt.id}
                    draggable onDragStart={e=>onDragStart(e,apt)} onDragEnd={onDragEnd}
                    onClick={e=>{e.stopPropagation();onOpenApt(apt);}}
                    onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();onTooltip({apt,x:r.right+8,y:r.top});}}
                    onMouseLeave={()=>onTooltip(null)}
                    style={{
                      position:"absolute",top,height,
                      left:`${info.col*colW+0.3}%`,width:`${colW-0.6}%`,
                      background:isCancelled?"var(--mf-bg-alt)":typ.bg||typ.color+"22",
                      border:`1px solid ${isCancelled?"var(--mf-border2)":typ.color}`,
                      borderLeft:`2px solid ${isCancelled?"#aaa":typ.color}`,
                      borderRadius:4,padding:"1px 3px",cursor:"grab",
                      opacity:isDragging?.3:1,zIndex:3,overflow:"hidden",boxSizing:"border-box",
                    }}
                  >
                    <div style={{fontSize:9,fontWeight:600,color:isCancelled?"#aaa":typ.color,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {apt.allergy&&!isCancelled&&<span style={{color:"var(--mf-neg)"}}>⚠ </span>}
                      {apt.patient.split(",")[0]||apt.patient.split(" ").pop()}
                    </div>
                    {height>20&&<div style={{fontSize:8,color:"var(--mf-muted)"}}>{getType(apt.type).label}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
